import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, DropdownMenu, IconButton } from '@radix-ui/themes';
import { useNavigate } from 'react-router-dom';
import { DotsHorizontalIcon } from '@/components/icons';
import {
  useNotificationList,
  useMarkNotificationRead,
  useMarkAllRead,
} from '../../hooks/useNotifications';
import api, { type PaginatedResult } from '../../services/api';
import { contentApi } from '../../services/content-api';
import type { Notification, ContentSummary } from '@echo-portal/shared';
import { NotificationItem } from './NotificationItem';
import styles from './NotificationList.module.css';

function getNotificationUrl(notification: Notification): string | null {
  const { resourceType, resourceId } = notification;
  if (!resourceId) return null;
  if (resourceType === 'branch') {
    // Section is finalised at click time by `resolveBranchSection` —
    // an asset branch wants `?section=assets` (so HeaderNav highlights
    // the Assets tab and the sidebar pulls asset categories), a
    // guideline branch keeps `?section=brands`. This sentinel just
    // tells handleNavigate "build the branch URL after a lookup".
    return 'branch';
  }
  // 'review' type requires async resolution — handled in handleNavigate
  if (resourceType === 'review') return 'resolve';
  return null;
}

/**
 * Spec 022 — detect the primary content section of a branch by
 * peeking at its first content row. Branches in practice carry either
 * all-asset or all-guideline content, so a single-row lookup is
 * sufficient to pick the right surface. Returns 'assets' for an
 * asset branch, 'brands' otherwise (back-compat default — matches
 * the historical hard-coded section).
 */
async function resolveBranchSection(
  branchId: string
): Promise<'brands' | 'products' | 'assets'> {
  try {
    const page: PaginatedResult<ContentSummary> = await contentApi.listByBranch({
      branchId,
      limit: 1,
    });
    const first = page.items[0];
    if (first?.contentType === 'asset') return 'assets';
    if (first?.section === 'product') return 'products';
    return 'brands';
  } catch {
    return 'brands';
  }
}

interface NotificationListProps {
  mode?: 'popover' | 'full';
  maxItems?: number;
  page?: number;
  onPageChange?: (page: number) => void;
  onClose?: () => void;
}

export function NotificationList({
  mode = 'full',
  maxItems,
  page = 1,
  onPageChange,
  onClose,
}: NotificationListProps) {
  const limit = mode === 'popover' ? (maxItems ?? 5) : 20;
  const { data, isLoading } = useNotificationList({ page, limit });
  const markReadMutation = useMarkNotificationRead();
  const markAllReadMutation = useMarkAllRead();
  const navigate = useNavigate();

  // IDs the user marked read during THIS open session. Drives the
  // soft `var(--gray-3)` trail on those rows so the user can see what
  // they just acted on. Cleared on unmount — popovers unmount their
  // content on close, so reopening starts fresh; the full-page list
  // accumulates across the session, which is the more useful default
  // there since the user is actively triaging.
  const [recentlyReadIds, setRecentlyReadIds] = useState<Set<string>>(
    () => new Set(),
  );
  useEffect(() => {
    // Clear when the list unmounts (popover closes).
    return () => setRecentlyReadIds(new Set());
  }, []);

  // Custom scroll-rail + indicator (popover mode only). Mirrors the
  // LibrarySidebar pattern: native scrollbar hidden via the CSS
  // module, a persistent 1-px gray rail at the right edge, and a
  // 2-px brand-orange marker that slides over the rail while
  // scrolling and fades after IDLE_HIDE_MS of idle. Driven by a
  // scroll listener + ResizeObserver so the indicator stays in sync
  // when content reflows (e.g. a new notification streams in).
  const scrollViewportRef = useRef<HTMLDivElement>(null);
  const railRef = useRef<HTMLDivElement>(null);
  const indicatorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const viewport = scrollViewportRef.current;
    const rail = railRef.current;
    const indicator = indicatorRef.current;
    if (!viewport || !rail || !indicator) return;

    let hideTimer: ReturnType<typeof setTimeout> | undefined;
    const IDLE_HIDE_MS = 800;
    // Floor on the indicator height — without this, a very long
    // unread list shrinks the marker to a sliver that's hard to
    // grab visually.
    const MIN_INDICATOR_H = 24;

    const update = () => {
      const { scrollTop, scrollHeight, clientHeight } = viewport;
      if (scrollHeight <= clientHeight || clientHeight === 0) {
        indicator.style.opacity = '0';
        indicator.style.height = '0px';
        return;
      }
      const rawH = (clientHeight / scrollHeight) * clientHeight;
      const indicatorH = Math.max(rawH, MIN_INDICATOR_H);
      const maxTop = clientHeight - indicatorH;
      const ratio = scrollTop / (scrollHeight - clientHeight);
      const top = ratio * maxTop;
      indicator.style.height = `${indicatorH}px`;
      indicator.style.transform = `translateY(${top}px)`;
    };

    const onScroll = () => {
      update();
      // Only fade in while there's something to scroll; `update()`
      // already clamps opacity to 0 in the early-return branch.
      if (viewport.scrollHeight > viewport.clientHeight) {
        indicator.style.opacity = '1';
        if (hideTimer) clearTimeout(hideTimer);
        hideTimer = setTimeout(() => {
          indicator.style.opacity = '0';
        }, IDLE_HIDE_MS);
      }
    };

    update();
    viewport.addEventListener('scroll', onScroll, { passive: true });
    // Re-measure on viewport size changes (popover open/close, font
    // load) and on content-size changes (rows added / removed by a
    // mutation refetch). Observing both gives clean coverage.
    const ro = new ResizeObserver(update);
    ro.observe(viewport);
    if (viewport.firstElementChild) ro.observe(viewport.firstElementChild);

    return () => {
      viewport.removeEventListener('scroll', onScroll);
      ro.disconnect();
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, []);

  const handleMarkRead = useCallback(
    (notificationId: string) => {
      markReadMutation.mutate(notificationId);
      setRecentlyReadIds((prev) => {
        const next = new Set(prev);
        next.add(notificationId);
        return next;
      });
    },
    [markReadMutation],
  );

  const handleMarkAllRead = useCallback(() => {
    markAllReadMutation.mutate();
    // Add every currently-unread row to the trail so the user gets a
    // single grouped visual confirmation. (The mutation flips them all
    // in one go; per-row recently-read state would be invisible since
    // each row also loses its unread dot at the same time.)
    setRecentlyReadIds((prev) => {
      const next = new Set(prev);
      (data?.items ?? []).forEach((n) => {
        if (!n.isRead) next.add(n.id);
      });
      return next;
    });
  }, [markAllReadMutation, data]);

  const handleNavigate = useCallback(
    async (notification: Notification) => {
      const url = getNotificationUrl(notification);
      if (!url) return;

      if (url === 'branch' && notification.resourceId) {
        // Spec 022 — peek the branch's first content row to decide
        // whether this is an asset or guideline branch; route to the
        // matching section so the navbar highlight + sidebar tree
        // line up with what the reviewer is about to see.
        const section = await resolveBranchSection(notification.resourceId);
        onClose?.();
        navigate(
          `/library?section=${section}&mode=review&branchId=${notification.resourceId}`
        );
        return;
      }

      if (url === 'resolve' && notification.resourceId) {
        // Resolve review UUID to branch ID, then navigate
        try {
          const review = await api.get<{
            branchId: string;
            comments: Array<{
              id: string;
              authorId: string;
              parentId?: string | null;
              createdAt: string;
            }>;
          }>(`/reviews/${notification.resourceId}`);
          onClose?.();
          const isCommentNotification =
            notification.type === 'review_comment_added' ||
            notification.type === 'review_comment_reply';
          if (isCommentNotification) {
            // Find the target comment: match actorId, pick most recent by createdAt
            const isReply = notification.type === 'review_comment_reply';
            const candidates = (review.comments || []).filter(
              (c) =>
                c.authorId === notification.actorId &&
                (isReply ? !!c.parentId : !c.parentId),
            );
            candidates.sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime(),
            );
            const targetComment = candidates[0];
            const commentParam = targetComment
              ? `&commentId=${targetComment.id}`
              : '';
            navigate(
              `/library?mode=review&branchId=${review.branchId}${commentParam}`,
            );
          } else {
            // Spec 022 — same asset-vs-guideline detection as the
            // direct-branch path. Otherwise an asset comment-less
            // review notification lands on /library?section=brands
            // with brand-section sidebar even though the change is
            // an asset.
            const section = await resolveBranchSection(review.branchId);
            navigate(
              `/library?section=${section}&mode=review&branchId=${review.branchId}`,
            );
          }
        } catch {
          // Review may have been deleted — silently ignore
        }
        return;
      }

      onClose?.();
      navigate(url);
    },
    [navigate, onClose],
  );

  const handleShowAll = useCallback(() => {
    onClose?.();
    navigate('/profile?tab=notifications');
  }, [navigate, onClose]);

  const notifications = data?.items ?? [];
  const total = data?.total ?? 0;
  const hasMore = data?.hasMore ?? false;
  const hasAny = notifications.length > 0;

  // Popover mode uses an explicit flex-column layout with a maxHeight
  // cap, so the middle scroll area gets the remaining height and is a
  // clear scroll container. Radix Themes' Popover.Content ships with
  // `overflow: auto` of its own (see `.rt-PopoverContent` in the Radix
  // stylesheet) — without this structure, wheel events route to the
  // popover's auto-scroll context instead of the inner list, and the
  // inner `overflow-y: auto` never gets the events. Full mode keeps
  // its previous block layout (the page handles scroll).
  const isPopover = mode === 'popover';

  return (
    <div
      style={
        isPopover
          ? {
              display: 'flex',
              flexDirection: 'column',
              // Total cap matches header (≈49px) + 320px scroll area +
              // footer (≈66px) = ~435px, which fits comfortably under
              // any normal-height viewport with the bell-anchored
              // popover positioned at top-right.
              maxHeight: 440,
            }
          : undefined
      }
    >
      {/* Header: title + overflow menu. flex-shrink: 0 so the scroll
          area can claim the remaining height without compressing this. */}
      <div
        className="flex items-center justify-between"
        style={{
          padding: '14px 16px',
          borderBottom: '1px solid var(--gray-6)',
          flexShrink: 0,
        }}
      >
        <h3
          className="text-base font-semibold"
          style={{ color: 'var(--gray-12)', margin: 0 }}
        >
          Activity Center
        </h3>

        <DropdownMenu.Root>
          <DropdownMenu.Trigger>
            <IconButton
              variant="ghost"
              color="gray"
              size="1"
              aria-label="Activity Center menu"
            >
              <DotsHorizontalIcon width={16} height={16} />
            </IconButton>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content align="end" sideOffset={4}>
            <DropdownMenu.Item
              disabled={!hasAny || markAllReadMutation.isPending}
              onSelect={handleMarkAllRead}
            >
              Mark all as read
            </DropdownMenu.Item>
            <DropdownMenu.Item
              onSelect={() => {
                onClose?.();
                navigate('/settings/notifications');
              }}
            >
              Notification settings
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      </div>

      {/* Scroll area. In popover mode this is wrapped in a relative
          container that hosts the custom rail + indicator (siblings
          of the scrolling viewport, NOT children — they must stay
          pinned to the visible edge, not slide with content). The
          native scrollbar is hidden via the CSS module so the rail
          can carry the affordance unopposed.
          `data-lenis-prevent` opts the inner viewport out of Lenis's
          wheel-event hijack on the landing page — without it, opening
          the tray on `/` left this list un-scrollable because Lenis
          was consuming the wheel events at the document level. */}
      {isPopover ? (
        <div className={styles.scrollWrap}>
          <div
            ref={scrollViewportRef}
            data-lenis-prevent
            className={styles.scrollViewport}
          >
            <NotificationListBody
              isLoading={isLoading}
              notifications={notifications}
              recentlyReadIds={recentlyReadIds}
              onMarkRead={handleMarkRead}
              onNavigate={handleNavigate}
            />
          </div>
          <div ref={railRef} className={styles.scrollRail} aria-hidden />
          <div
            ref={indicatorRef}
            className={styles.scrollIndicator}
            aria-hidden
          />
        </div>
      ) : (
        <NotificationListBody
          isLoading={isLoading}
          notifications={notifications}
          recentlyReadIds={recentlyReadIds}
          onMarkRead={handleMarkRead}
          onNavigate={handleNavigate}
        />
      )}

      {/* Footer — full-width "View All Activity" pill (popover only).
          flex-shrink: 0 keeps the footer pinned at the bottom of the
          440-px column while the middle scroll area absorbs any extra
          rows. */}
      {mode === 'popover' && (
        <div
          style={{
            padding: '12px 16px 16px',
            borderTop: '1px solid var(--gray-6)',
            flexShrink: 0,
          }}
        >
          <Button
            variant="soft"
            color="gray"
            size="2"
            onClick={handleShowAll}
            style={{ width: '100%', borderRadius: 10 }}
          >
            View All Activity
          </Button>
        </div>
      )}

      {/* Full-mode pagination (unchanged from prior implementation) */}
      {mode === 'full' && total > limit && (
        <div
          className="flex items-center justify-between"
          style={{
            padding: '12px 16px',
            borderTop: '1px solid var(--gray-6)',
          }}
        >
          <Button
            variant="ghost"
            size="1"
            disabled={page <= 1}
            onClick={() => onPageChange?.(page - 1)}
          >
            Previous
          </Button>
          <span className="text-sm" style={{ color: 'var(--gray-9)' }}>
            Page {page} of {Math.ceil(total / limit)}
          </span>
          <Button
            variant="ghost"
            size="1"
            disabled={!hasMore}
            onClick={() => onPageChange?.(page + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Renders the loading state, empty state, OR the list of
 * NotificationItem rows. Used by both the popover (wrapped in the
 * custom-scrollbar scroll area) and the full-mode path (rendered
 * inline; the page provides scroll).
 *
 * Important: this stays mounted whether loading or not so
 * NotificationList's scroll-rail refs attach to stable DOM elements
 * on first render. Returning a loading sentinel above this layer
 * caused the scroll-indicator effect to bail (refs were null) and
 * never re-attach, so the orange marker never appeared after the
 * initial fetch resolved.
 */
function NotificationListBody({
  isLoading,
  notifications,
  recentlyReadIds,
  onMarkRead,
  onNavigate,
}: {
  isLoading: boolean;
  notifications: Notification[];
  recentlyReadIds: Set<string>;
  onMarkRead: (id: string) => void;
  onNavigate: (notification: Notification) => void;
}) {
  if (isLoading) {
    return (
      <div
        className="p-4 text-sm"
        style={{ color: 'var(--gray-9)' }}
      >
        Loading notifications…
      </div>
    );
  }
  if (notifications.length === 0) {
    return (
      <div style={{ padding: '32px 16px', textAlign: 'center' }}>
        <p
          className="text-sm"
          style={{ margin: 0, color: 'var(--gray-12)', fontWeight: 500 }}
        >
          You&apos;re all caught up
        </p>
        <p
          className="text-xs"
          style={{ margin: '4px 0 0', color: 'var(--gray-9)' }}
        >
          New activity will show up here.
        </p>
      </div>
    );
  }
  return (
    <div>
      {notifications.map((notification, idx) => (
        <NotificationItem
          key={notification.id}
          notification={notification}
          recentlyRead={recentlyReadIds.has(notification.id)}
          isLast={idx === notifications.length - 1}
          onMarkRead={onMarkRead}
          onNavigate={onNavigate}
        />
      ))}
    </div>
  );
}

export default NotificationList;
